import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { TableSortColumn } from 'src/modules/sm-base/models/table-sort-column.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from "src/modules/utils/shared/objects";
import { EnaioCertificateService } from '../../services/enaio-certificate.service';
import { ClassRegister } from '../../shared/class-register.entity';
import { ClassTeacher } from '../../shared/class-teacher.entity';
import { SchoolType } from '../../shared/school-type.enum';
import { Teacher } from '../../shared/teacher.model';

@Component({
    selector: 'app-class-teacher',
    templateUrl: './class-teacher.component.html',
    styleUrls: ['./class-teacher.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassTeacherComponent extends ComponentView {

    service = inject(EnaioCertificateService);

    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id: number;

    schoolClassId: number;
    domains: string[] = [];
    teachers: ClassTeacher[] = [];
    classTeachers: Teacher[];
    teachersGui: FrontendFieldListItem[];
    allowedTeachers: string[] = [];
    classRegister: ClassRegister;
    isSecondary: boolean;

    teacherEntries: ClassTeacher[] = [];
    orgTeacherEntries: ClassTeacher[] = [];
    table: TableData;

    async initParams(): Promise<boolean> {
        this.classRegister = await this.service.restGetClassRegister(this.id, ["teachers"], null, true);
        this.classTeachers = [new Teacher("", ""), ...await this.service.restGetTeachers(this.classRegister.schoolClassId)
        ];
        let settings = await this.service.restGetClassRegisterSettings(this.classRegister.enaioSchoolClass.getYearNumber(), this.classRegister.isHalfYear);
        this.domains = await this.service.restGetDomains(this.classRegister.enaioSchoolClass.grade, this.classRegister.enaioSchoolClass.getSchoolType(), true, null, settings);
        this.isSecondary = this.classRegister.enaioSchoolClass.getSchoolType() == SchoolType.secondary && this.classRegister.enaioSchoolClass.grade >= 7;

        let schoolType = this.classRegister.enaioSchoolClass.getSchoolType();
        let ta = settings.teacher.filter(s => s.isForSchoolType(schoolType));
        this.allowedTeachers = ta.map(t => t.userName);
        this.teachersGui = FrontendFieldListItem.make(this.classTeachers.filter(t => t.userName == "" || this.allowedTeachers.includes(t.userName)), e => [e.userName, e.fullName]);

        this.teacherEntries = this.domains.filter(d => d != "").map(d => Objects.fromPlain(ClassTeacher, { domain: d}));
        for (let entry of this.classRegister.teachers) {
            this.teacherEntries[this.teacherEntries.findIndex(e => e.domain == entry.domain)] = entry;
        }
        let props: Partial<TableColumn> = { 
            editable: true, 
            editor: new FrontendField("", "", FrontendFieldType.dropdown, { listItems: this.teachersGui}),
            valueFormatter: e => this.getTeacherByUserName(e.value as string) 
        };
        this.table = new TableData([
            new TableColumn("domain", "Unterrichtsfach", TableCellType.string, 20),
            new TableColumn("teacher", "Erste Lehrkraft " + (this.isSecondary ? " gymnasial" : ""), TableCellType.string, 20, Objects.cloneDeep(props)),
            new TableColumn("teacherSecond", "Zweite Lehrkraft " + (this.isSecondary ? " gymnasial" : ""), TableCellType.string, 20, Objects.cloneDeep(props)),
            this.isSecondary ? new TableColumn("teacherRegional", "Erste Lehrkraft regional", TableCellType.string, 20, Objects.cloneDeep(props)) : null,
            this.isSecondary ? new TableColumn("teacherRegionalSecond", "Zweite Lehrkraft regional", TableCellType.string, 20, Objects.cloneDeep(props)) : null
        ], this.teacherEntries.map(te => new TableRow(te.domain, te, te)), ["domain"]);
        
        this.orgTeacherEntries = Objects.cloneDeep(this.teacherEntries);

        this.app.updateNavigation(null, [
            { label: "Digitales Klassenbuch", routerLink: ["/enaio-certificates", "class-registers", "home"] },
            { label: "Klasse " + this.classRegister.enaioSchoolClass.getDisplayName(), routerLink: ["/enaio-certificates", "class-registers", "class-register", this.id] },
            { label: "Klassentagebuch", routerLink: ["/enaio-certificates", "class-registers", "teacher", this.id] }
        ]);

        return true;
    }

    override getCanDeactivateMessage(): string {
        return this.isChanged() ? "" : null;
    }

    isChanged(): boolean {
        return !Objects.equalsDeep(this.teacherEntries, this.orgTeacherEntries);
    }

    async save(): Promise<void> {
        await this.app.saveDataHandler(async () => RestEndpoint.main().query({ id: this.id }).body(this.teacherEntries).post().run("api/cer/classregister/teachers").getText(), this, true, true);
    }

    getTeacherByUserName(username: string): string {
        return this.classTeachers.find(t => t.userName == username).fullName;
    }
}
